import { Card, colors, Grid, H3, Row, Skeleton, usePageWidth } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAtPageContentSize } from '../PageContentWidthContext'
import FamilyMembersCardSkeleton from '../cards/family-members-card/FamilyMembersCardSkeleton'
import OtherInformationCardSkeleton from '../cards/other-information-card/OtherInformationCardSkeleton'
import OtherRelationshipsCardSkeleton from '../cards/other-relationships-card/OtherRelationshipsCardSkeleton'
import VitalsCardSkeleton from '../cards/vitals-card/VitalsCardSkeleton'
import { SOURCE_CENTRIC_SECTION_IDS, type VisibleSectionId } from './sourceCentricDetailsTabHelpers'

// Lightweight stand-in for the source rail's loading state, built only from @fs/zion-ui primitives.
// Rendering the real SourceRail here would statically pull @fs/zion-source-list into the initial
// chunk for every visitor (flag on or off); the real SourceRail loads with the lazy source-centric tab.
function SourceRailPlaceholder(): JSX.Element {
  return (
    <Grid gutters="xs" data-testid="source-rail-placeholder">
      {[0, 1, 2].map((index) => (
        <Card key={index} backgroundColor={colors.gray00} edges="none" margins="xxs">
          <Row alignY="middle" gutters="xs">
            <Row gutters="xs" growX>
              <Skeleton.Icon size="sm" noShrinkX alignY="middle" />
              <Skeleton.Paragraph size="sm" growX lineCount={2} />
            </Row>
            <Skeleton.Button width={120} noShrinkX data-testid="source-rail-placeholder-action" />
          </Row>
        </Card>
      ))}
    </Grid>
  )
}

export default function SourceCentricDetailsTabSkeleton(): JSX.Element {
  const [t] = useTranslation()
  const pageWidth = usePageWidth()
  const atWidth = useAtPageContentSize()
  const showSecondaryCards = pageWidth({ default: false, lg: true })
  const moveSecondaryCards = atWidth({ default: showSecondaryCards, lg: false })
  const gridGutters = 'sm'

  const secondaryCardsColumns = (() => {
    if (moveSecondaryCards) return 12
    if (showSecondaryCards) return 4
    return 'hide'
  })()
  const primaryCardsColumns = moveSecondaryCards || !showSecondaryCards ? 12 : 8

  const sectionOrder: VisibleSectionId[] = [...SOURCE_CENTRIC_SECTION_IDS]

  const sectionSkeletonMap = {
    vitals: <VitalsCardSkeleton key="vitals" totalColumns={primaryCardsColumns} isSingleCardPage={false} />,
    'other-information': (
      <OtherInformationCardSkeleton
        key="other-information"
        totalColumns={primaryCardsColumns}
        isSingleCardPage={false}
      />
    ),
    'family-members': (
      <FamilyMembersCardSkeleton key="family-members" totalColumns={primaryCardsColumns} isSingleCardPage={false} />
    ),
    'other-relationships': (
      <OtherRelationshipsCardSkeleton
        key="other-relationships"
        totalColumns={primaryCardsColumns}
        isSingleCardPage={false}
      />
    ),
  } satisfies Record<VisibleSectionId, JSX.Element>

  return (
    <Grid gutters="nano">
      <Grid gutters={gridGutters}>
        <Grid gutters={gridGutters} columns={primaryCardsColumns} data-testid="left-column">
          {sectionOrder.map((sectionId) => sectionSkeletonMap[sectionId]).filter(Boolean)}
        </Grid>

        <Grid gutters="xs" columns={secondaryCardsColumns} data-testid="right-column">
          <H3 size="H5">{t('tree-person.sources.header')}</H3>
          <SourceRailPlaceholder />
        </Grid>
      </Grid>
    </Grid>
  )
}
